// OrgChartMinimap — scaled-down overview of the chart with a live viewport
// rectangle; clicking jumps that point to the center of the canvas.

import { useTranslation } from 'react-i18next';
import { getLevel } from '@/mocks/structure';
import { ChartLayout, MINI_H, MINI_PAD, MINI_W } from './orgChartLayout';
import { Transform } from './useOrgChart';

interface OrgChartMinimapProps {
  layout: ChartLayout;
  t: Transform;
  viewW: number;
  viewH: number;
  onJump: (cx: number, cy: number) => void;
}

const OrgChartMinimap = ({ layout, t, viewW, viewH, onJump }: OrgChartMinimapProps) => {
  const { t: tr } = useTranslation();
  const s = Math.min(
    (MINI_W - MINI_PAD * 2) / layout.width,
    (MINI_H - MINI_PAD * 2) / layout.height,
  );
  const ox = (MINI_W - layout.width * s) / 2;
  const oy = (MINI_H - layout.height * s) / 2;
  const vx = -t.x / t.s;
  const vy = -t.y / t.s;
  const vw = viewW / t.s;
  const vh = viewH / t.s;

  const jump = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    onJump((e.clientX - r.left - ox) / s, (e.clientY - r.top - oy) / s);
  };

  return (
    <div
      className='org-chart__mini'
      role='button'
      aria-label={tr('structure_chart_minimap')}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={jump}
    >
      <svg width={MINI_W} height={MINI_H}>
        <g transform={`translate(${ox},${oy}) scale(${s})`}>
          {layout.nodes.map((r) => (
            <rect
              key={r.id}
              className={`org-chart__mini-rect org-chart__mini-rect--${getLevel(r.node.level).tone}`}
              x={r.x}
              y={r.y}
              width={r.w}
              height={r.h}
              rx={10}
              strokeWidth={2.5}
            />
          ))}
        </g>
        <rect
          className='org-chart__mini-viewport'
          x={ox + vx * s}
          y={oy + vy * s}
          width={vw * s}
          height={vh * s}
          rx={3}
          strokeWidth={1.5}
        />
      </svg>
    </div>
  );
};

export default OrgChartMinimap;
